// Node call-sign registry for TTR verification attribution.
//
// A TTR code's 4th segment is the NODE token:
//     {BOARD}-{POS}-{GRADE}-{NODE}-{SEQ}
//     7677TOPPSBB-001-RAW-SCHMIDT-001   → node token "SCHMIDT" (legacy long form)
//     7677TOPPSBB-001-RAW-SCH-001       → node token "SCH"     (3-letter, going forward)
//
// Going forward, nodes get a 3-letter, foundation-assigned, NASDAQ-style call
// sign (SCH = Schmidt). We still accept the legacy long form so older stickers
// keep resolving. The node token is for ATTRIBUTION/DISPLAY only — it is never a
// resolution blocker (resolution is on BOARD + POS; see useVerification.ts). This
// registry just turns whichever token was scanned into "Schmidt Auctions (SCH)".
//
// Seed with Schmidt for now; add nodes here as they're onboarded.

export interface NodeInfo {
  /** Canonical 3-letter call sign (e.g. "SCH"). */
  callSign: string;
  /** Human display name (e.g. "Schmidt Auctions"). */
  displayName: string;
}

/** Canonical call sign → node info. */
const NODE_REGISTRY: Record<string, NodeInfo> = {
  SCH: { callSign: 'SCH', displayName: 'Schmidt Auctions' },
};

/** Legacy / long-form / alias tokens → canonical call sign. */
const NODE_ALIASES: Record<string, string> = {
  SCHMIDT: 'SCH',
};

/**
 * Resolve a scanned node token to its registered NodeInfo, tolerating the legacy
 * long form. Returns null for unknown tokens so callers can fall back.
 */
export function resolveNode(token: string | null | undefined): NodeInfo | null {
  if (!token) return null;
  const t = token.trim().toUpperCase();
  if (NODE_REGISTRY[t]) return NODE_REGISTRY[t];
  const canonical = NODE_ALIASES[t];
  if (canonical && NODE_REGISTRY[canonical]) return NODE_REGISTRY[canonical];
  return null;
}

export interface NodeAttribution {
  /** e.g. "Schmidt Auctions" */
  displayName: string;
  /** e.g. "SCH" — may be empty when unknown and no token was scanned. */
  callSign: string;
}

function titleCase(token: string): string {
  return token
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Produce a display-ready attribution ("Registered by <displayName> (<callSign>)").
 *
 * Prefers the registry; if the scanned token is unknown it falls back to the
 * mirror doc's holder name (when provided) and derives a short call sign from the
 * token so the page still attributes the item to *something* sensible.
 *
 * @param token         The scanned NODE segment (e.g. "SCHMIDT" or "SCH").
 * @param fallbackName  Optional holder display name from the mirror doc.
 * @returns NodeAttribution, or null if there's nothing at all to show.
 */
export function formatNodeAttribution(
  token: string | null | undefined,
  fallbackName?: string | null,
): NodeAttribution | null {
  const t = token ? token.trim().toUpperCase() : '';

  const info = resolveNode(t);
  if (info) return { displayName: info.displayName, callSign: info.callSign };

  if (!t) {
    return fallbackName ? { displayName: fallbackName, callSign: '' } : null;
  }

  // Unknown node token — attribute to the holder name if we have one, and derive
  // a short call sign from the token (already 3-letter, or first 3 chars).
  return {
    displayName: fallbackName || titleCase(t),
    callSign: t.length <= 4 ? t : t.slice(0, 3),
  };
}
